<template>
  <div>
    <p class="mb-8">
      The compatibility form will help CHI learn about your lifestyle, family
      characteristics and preferences to suggest your best au pair matches.
      Although the questions are not mandatory, we encourage you to answer all
      of them, as best as you can.
    </p>

    <ChiCard className="p-12 animeLeft">
      <form>
        <!-- Family Lifetyle -->
        <fieldset>
          <legend class="font-semibold text-2xl">Family Lifestyle</legend>
          <p class="text-sm leading-4">
            What does your family like to do together? Choose as many options as
            you would like.
          </p>

          <div class="mt-3 flex flex-wrap items-center justify-between">
            <p-check
              v-for="(item, index) in characteristics.lifestyle"
              :key="index"
              class="text-lg md:text-base p-icon p-smooth tracking-tight w-full sm:w-1/3 md:w-1/4 md:my-3 my-5"
              color="success"
              v-model="item.value"
            >
              <i slot="extra" class="icon material-icons">done</i>
              {{ item.key }}
            </p-check>
          </div>
        </fieldset>

        <!-- Family Description -->
        <fieldset>
          <legend class="font-semibold text-2xl">
            How would you describe your family?
          </legend>
          <p class="text-sm leading-4">
            Rate from 1 to 5 each of the characteristics below, 5 being the
            highest.
          </p>

          <div class="mt-3 flex flex-wrap items-center justify-between">
            <div
              class="w-full sm:w-1/3 my-3"
              v-for="(item,
              index) in characteristics.characteristicsDescription"
              :key="index"
            >
              <span>{{ item.key }}</span>

              <star-rating
                v-model="item.value"
                :star-size="25"
                :star-points="[
                  23,
                  2,
                  14,
                  17,
                  0,
                  19,
                  10,
                  34,
                  7,
                  50,
                  23,
                  43,
                  38,
                  50,
                  36,
                  34,
                  46,
                  19,
                  31,
                  17,
                ]"
                active-color="#9A69CB"
                :clearable="true"
              ></star-rating>
            </div>
          </div>
        </fieldset>

        <!-- Religion -->
        <fieldset>
          <legend class="font-semibold text-2xl">
            Religion
          </legend>

          <div class="mt-3">
            <div class="w-full">
              <label for="desirableCountries"
                >What is your religious affiliation?
              </label>
              <multiselect
                id="desirableCountries"
                v-model="characteristics.belief"
                :options="listOfReligions"
                :close-on-select="true"
                :searchable="true"
                placeholder="Please select"
              ></multiselect>
            </div>
          </div>
        </fieldset>

        <!-- Smoking -->
        <fieldset>
          <legend class="font-semibold text-2xl">
            Smoking
          </legend>

          <div class="mt-3">
            <label>Does any member of your family smokes?</label>
            <div class="flex items-center justify-start mt-4">
              <p-radio
                v-model="smokingFamily"
                class="p-icon p-round"
                value="true"
                name="smokingFamily"
                color="success"
              >
                <i slot="extra" class="icon material-icons">done</i>
                Yes
              </p-radio>

              <p-radio
                v-model="smokingFamily"
                class="p-icon p-round"
                value="no"
                name="smokingFamily"
                color="success"
              >
                <i slot="extra" class="icon material-icons">done</i>
                No
              </p-radio>
            </div>
          </div>

          <div v-if="smokingFamily && smokingFamily !== 'no'" class="mt-3">
            <div class="flex items-center justify-start mt-4">
              <p-radio
                v-model="characteristics.smoking"
                class="p-icon p-round"
                value="inside"
                name="smokingLocation"
                color="success"
              >
                <i slot="extra" class="icon material-icons">done</i>
                Inside
              </p-radio>

              <p-radio
                v-model="characteristics.smoking"
                class="p-icon p-round"
                value="outside"
                name="smokingLocation"
                color="success"
              >
                <i slot="extra" class="icon material-icons">done</i>
                Outside
              </p-radio>
            </div>
          </div>
        </fieldset>

        <!-- Alcohol Consumption -->
        <fieldset>
          <legend class="font-semibold text-2xl">
            Alcohol Consumption
          </legend>

          <div class="mt-3">
            <label>Is alcohol consumption allowed at the family's house?</label>
            <div class="flex items-center justify-start mt-4">
              <p-radio
                v-model="characteristics.alcohol"
                class="p-icon p-round"
                :value="true"
                name="alcoholConsumption"
                color="success"
              >
                <i slot="extra" class="icon material-icons">done</i>
                Yes
              </p-radio>

              <p-radio
                v-model="characteristics.alcohol"
                class="p-icon p-round"
                :value="false"
                name="alcoholConsumption"
                color="success"
              >
                <i slot="extra" class="icon material-icons">done</i>
                No
              </p-radio>
            </div>
          </div>
        </fieldset>

        <!-- Pets -->
        <fieldset>
          <legend class="font-semibold text-2xl">
            Pets
          </legend>

          <div class="mt-3">
            <label>Does your family have any pet(s)?</label>

            <div class="mt-3 flex flex-wrap items-center justify-between">
              <p-check
                v-for="(item, index) in characteristics.pets"
                :key="index"
                class="text-lg md:text-medium p-icon p-smooth tracking-tight w-full sm:w-1/5 my-3"
                color="success"
                v-model="item.value"
              >
                <i slot="extra" class="icon material-icons">done</i>
                {{ item.key }}
              </p-check>
            </div>
          </div>
        </fieldset>
      </form>

      <div class="flex items-end justify-end mt-12">
        <button
          @click.prevent="submitForm()"
          href=""
          class="btn btn-purple"
          :disabled="loading"
        >
          NEXT: Your Ideal Au Pair Characteristics
        </button>
      </div>

      <progress-bar
        class="progress"
        size="huge"
        :bar-color="`${progress === 100 ? '#48BB78' : '#553C9A'}  `"
        text-fg-color="#fff"
        bg-color="#CDB9E1"
        text-position="middle"
        :val="progress"
        :text="`${progress >= 100 ? '100' : progress}%`"
      ></progress-bar>
    </ChiCard>
  </div>
</template>

<script>
import ChiCard from "@/components/UI/ChiCard.vue";
import ProgressBar from "vue-simple-progress";
import Multiselect from "vue-multiselect";

export default {
  name: "HostFamilyCharacteristics",
  components: {
    ChiCard,
    Multiselect,
    ProgressBar,
  },
  props: ["characteristics"],
  data() {
    return {
      loading: false,
      progress: 0,
      progressValues: {
        lifeStyle: 0,
        familyDescription: 0,
        religion: 0,
        smokingFamily: 0,
        alcoholConsumption: 0,
      },
      smokingFamily:
        this.characteristics.smoking !== "no" &&
        this.characteristics.smoking !== null
          ? true
          : this.characteristics.smoking,
      listOfReligions: [
        "Christianity",
        "Islam",
        "Agnostic",
        "Atheist",
        "Hinduism",
        "Chinese",
        "Buddhism",
        "Primal Indigenous",
        "African",
        "Sikhism",
        "Juche",
        "Spiritism",
        "Judaism",
        "Bahai",
        "Jainism",
        "Shinto",
        "Cao",
        "Zoroastrianism",
        "Tenrikyo",
        "NeoPaganism",
        "UnitarianUniversalism",
      ],
    };
  },
  watch: {
    characteristics: {
      deep: true,
      handler: function(val) {
        this.handleProgress(val);
      },
    },
    smokingFamily: {
      deep: true,
      handler: function(val) {
        if (val === "no") {
          this.characteristics.smoking = "no";
        }
        this.handleSmokingProgress(val);
      },
    },
  },
  methods: {
    submitForm() {
      window.localStorage.setItem("compatibilityCTA", "false");
      this.loading = true;
      this.$emit("saveFamilyCharacteristics");
    },
    handleProgress(characteristics) {
      characteristics.lifestyle.filter((item) => item.value).length > 0
        ? (this.progressValues.lifeStyle = 25)
        : (this.progressValues.lifeStyle = 0);

      const amountOfAnswers = characteristics.characteristicsDescription.reduce(
        (acc, item) => {
          if (item.value > 0) {
            acc += 3;
          }
          return acc;
        },
        0
      );

      this.progressValues.familyDescription = amountOfAnswers;

      this.progressValues.religion = characteristics.belief !== null ? 10 : 0;

      this.progressValues.alcoholConsumption =
        characteristics.alcohol !== null ? 10 : 0;

      this.calcProgress();
    },
    handleSmokingProgress(val) {
      if (val === null) {
        this.progressValues.smokingFamily = 0;
      } else {
        this.progressValues.smokingFamily = 10;
      }

      this.calcProgress();
    },
    calcProgress() {
      const values = Object.values(this.progressValues);
      const progress = values.reduce((acc, val) => {
        acc = acc + val;
        return acc;
      }, 0);

      this.progress = progress;
    },
  },
  created() {
    if (this.characteristics) {
      this.handleProgress(this.characteristics);
      this.handleSmokingProgress(this.smokingFamily);
    }
  },
};
</script>

<style scoped lang="scss">
fieldset::after {
  content: "";
  width: 100%;
  height: 2px;
  @apply bg-purple-ap-t03;
  position: relative;
  display: block;
  margin: 20px 0;
}

fieldset:last-child::after {
  display: none;
}

.progress {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100vw;
  z-index: 99999999;
}
</style>
